import { useState, useEffect, FormEvent } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';

// ── Types ─────────────────────────────────────────────────────────────────────

type Tab = 'profile' | 'company' | 'password' | 'privacy' | 'language';

type Profile = {
  id: number; email: string; firstName: string; lastName: string;
  phone: string; companyName: string; companyDomain: string; country: string;
};

type Member = {
  userId: number; role: string;
  user: { id: number; email: string; firstName: string | null; lastName: string | null };
};

type JoinRequest = {
  id: number; createdAt: string;
  user: { id: number; email: string; firstName: string | null; lastName: string | null; companyName: string | null };
};

type Company = {
  id: number; name: string; vatNumber: string | null; country: string | null;
  koppelCode?: string; myRole: string | null; companyUsers: Member[];
};

type PendingInvite = { id: number; email: string; expiresAt: string; createdAt: string };

const COUNTRIES = [
  'Afghanistan','Albania','Algeria','Angola','Argentina','Armenia','Australia','Austria','Azerbaijan',
  'Bahrain','Bangladesh','Belgium','Bolivia','Bosnia & Herzegovina','Brazil','Bulgaria',
  'Cambodia','Cameroon','Canada','Chile','China','Colombia','Croatia','Cyprus','Czechia',
  'Denmark','Ecuador','Egypt','Estonia','Ethiopia','Finland','France','Georgia','Germany',
  'Ghana','Greece','Guatemala','Honduras','Hungary','Iceland','India','Indonesia','Iraq',
  'Ireland','Israel','Italy','Jamaica','Japan','Jordan','Kazakhstan','Kenya','Kuwait',
  'Latvia','Lebanon','Libya','Lithuania','Luxembourg','Malaysia','Mexico','Moldova',
  'Morocco','Mozambique','Myanmar','Nepal','Netherlands','New Zealand','Nigeria','Norway',
  'Oman','Pakistan','Panama','Paraguay','Peru','Philippines','Poland','Portugal','Qatar',
  'Romania','Russia','Saudi Arabia','Senegal','Serbia','Singapore','Slovakia','Slovenia',
  'South Africa','South Korea','Spain','Sri Lanka','Sudan','Sweden','Switzerland','Taiwan',
  'Tanzania','Thailand','Tunisia','Turkey','Uganda','Ukraine','United Arab Emirates',
  'United Kingdom','United States','Uruguay','Venezuela','Vietnam','Zimbabwe',
];

// ── Main component ────────────────────────────────────────────────────────────

export default function Settings() {
  const { user: authUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = (searchParams.get('tab') as Tab) ?? 'profile';
  const setTab = (t: Tab) => setSearchParams({ tab: t }, { replace: true });

  const { t } = useTranslation();
  const TABS: { id: Tab; label: string }[] = [
    { id: 'profile',  label: t('settings.profile')   },
    { id: 'company',  label: t('settings.company')   },
    { id: 'password', label: t('settings.security')  },
    { id: 'privacy',  label: 'Privacy'               },
    { id: 'language', label: t('settings.language')  },
  ];

  return (
    <Layout>
      <div className="max-w-2xl mx-auto">
        <h1 className="text-2xl font-bold mb-6" style={{ color: '#0b0d0e' }}>Settings</h1>

        {/* Tab bar */}
        <div className="flex gap-0 mb-8 border-b" style={{ borderColor: '#e5e5e5', overflowX: 'auto', WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none' }}>
          {TABS.map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className="px-4 py-2.5 text-sm font-medium transition-colors"
              style={{
                color: tab === t.id ? '#0b0d0e' : '#8f9b9b',
                borderBottom: tab === t.id ? '2px solid #e00000' : '2px solid transparent',
                marginBottom: -1,
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                flexShrink: 0,
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === 'profile'  && <ProfileTab />}
        {tab === 'company'  && <CompanyTab authUserId={authUser?.id} />}
        {tab === 'password' && <PasswordTab />}
        {tab === 'privacy'  && <PrivacyTab />}
        {tab === 'language' && <LanguageTab />}
      </div>
    </Layout>
  );
}

// ── Profile tab ───────────────────────────────────────────────────────────────

function ProfileTab() {
  const [profile, setProfile] = useState<Profile>({ id: 0, email: '', firstName: '', lastName: '', phone: '', companyName: '', companyDomain: '', country: '' });
  const [status, setStatus] = useState<{ type: 'success' | 'error'; msg: string } | null>(null);
  const [loading, setLoading] = useState(false);
  const [companyHint, setCompanyHint] = useState<string | null>(null);

  useEffect(() => {
    api.get<Profile>('/account/me').then(d => setProfile({
      id: d.id, email: d.email ?? '', firstName: d.firstName ?? '', lastName: d.lastName ?? '',
      phone: d.phone ?? '', companyName: d.companyName ?? '', companyDomain: d.companyDomain ?? '', country: d.country ?? '',
    })).catch(() => {});
  }, []);

  async function save(e: FormEvent) {
    e.preventDefault(); setLoading(true); setStatus(null);
    try {
      await api.patch('/account/me', { firstName: profile.firstName, lastName: profile.lastName, phone: profile.phone, companyName: profile.companyName, companyDomain: profile.companyDomain, country: profile.country });
      setStatus({ type: 'success', msg: 'Profile updated.' });
    } catch (e: any) {
      setStatus({ type: 'error', msg: e.message ?? 'Failed.' });
    } finally { setLoading(false); }
  }

  const set = (k: keyof Profile) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setProfile(p => ({ ...p, [k]: e.target.value }));

  async function checkCompanyName() {
    const name = profile.companyName.trim();
    if (name.length < 2) { setCompanyHint(null); return; }
    try {
      const res = await api.get<{ exists: boolean; companyName: string | null }>(`/companies/check?name=${encodeURIComponent(name)}`);
      if (res.exists && res.companyName !== profile.companyName) {
        setCompanyHint(res.companyName ?? name);
      } else {
        setCompanyHint(null);
      }
    } catch { setCompanyHint(null); }
  }

  return (
    <form onSubmit={save} className="space-y-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div><label className="label">First name</label><input className="input" value={profile.firstName} onChange={set('firstName')} /></div>
        <div><label className="label">Last name</label><input className="input" value={profile.lastName} onChange={set('lastName')} /></div>
      </div>
      <div>
        <label className="label">Email</label>
        <input className="input" value={profile.email} disabled style={{ opacity: 0.6, cursor: 'not-allowed' }} />
        <p className="text-xs mt-1" style={{ color: '#8f9b9b' }}>Email cannot be changed. Contact support if needed.</p>
      </div>
      <div><label className="label">Phone</label><input className="input" type="tel" value={profile.phone} placeholder="+1 555 000 0000" onChange={set('phone')} /></div>
      <div>
        <label className="label">Company name</label>
        <input className="input" value={profile.companyName} placeholder="Acme Corp"
          onChange={e => { set('companyName')(e); setCompanyHint(null); }}
          onBlur={checkCompanyName} />
        {companyHint && (
          <div className="mt-2 p-3 rounded-lg text-sm" style={{ background: '#fff7ed', border: '1px solid #fed7aa' }}>
            <p className="font-semibold mb-0.5" style={{ color: '#9a3412' }}>
              <strong>{companyHint}</strong> already has a portal account.
            </p>
            <p style={{ color: '#c2410c', fontSize: 13 }}>
              A company with this name already has a portal account. You may want to join them via Settings → Company instead of creating a duplicate.
            </p>
          </div>
        )}
      </div>
      <div>
        <label className="label">Company domain <span style={{ color: '#8f9b9b', fontWeight: 400 }}>(optional)</span></label>
        <input className="input" value={profile.companyDomain} placeholder="acmecorp.com" onChange={set('companyDomain')} />
      </div>
      <div>
        <label className="label">Country</label>
        <select className="input" value={profile.country} onChange={set('country')}>
          <option value="">Select your country…</option>
          {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <p className="text-xs mt-1" style={{ color: '#8f9b9b' }}>Used to show your local Montipower distributor on the Support page.</p>
      </div>
      {status && <div className={status.type === 'success' ? 'alert-success' : 'alert-error'}>{status.msg}</div>}
      <button className="btn-primary" type="submit" disabled={loading}>{loading ? 'Saving…' : 'Save changes'}</button>
    </form>
  );
}

// ── Company tab ───────────────────────────────────────────────────────────────

function CompanyTab({ authUserId }: { authUserId?: number }) {
  const [company, setCompany] = useState<Company | null | undefined>(undefined); // undefined = loading
  const [requests, setRequests] = useState<JoinRequest[]>([]);
  const [codeCopied, setCodeCopied] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [removingId, setRemovingId] = useState<number | null>(null);
  const [processingId, setProcessingId] = useState<number | null>(null);

  // Invite by email state
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviting, setInviting] = useState(false);
  const [inviteStatus, setInviteStatus] = useState<{ type: 'success' | 'error'; msg: string } | null>(null);
  const [pendingInvites, setPendingInvites] = useState<PendingInvite[]>([]);
  const [revokingId, setRevokingId] = useState<number | null>(null);

  // Setup wizard state
  const [setupTab, setSetupTab] = useState<'create' | 'join'>('create');
  const [createForm, setCreateForm] = useState({ name: '', vatNumber: '', confirmedRep: false });
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState('');
  const [koppelCode, setKoppelCode] = useState('');
  const [joinLoading, setJoinLoading] = useState(false);
  const [joinError, setJoinError] = useState('');
  const [joinSuccess, setJoinSuccess] = useState('');

  const isOwner = company?.myRole === 'owner';

  async function load() {
    try {
      const c = await api.get<Company | null>('/companies/my');
      setCompany(c);
      if (c?.myRole === 'owner') {
        const r = await api.get<JoinRequest[]>('/companies/my/requests').catch(() => []);
        setRequests(r);
        const inv = await api.get<PendingInvite[]>('/companies/my/invites').catch(() => []);
        setPendingInvites(inv);
      }
    } catch { setCompany(null); }
  }

  useEffect(() => { load(); }, []);

  async function copyCode() {
    if (!company?.koppelCode) return;
    await navigator.clipboard.writeText(company.koppelCode);
    setCodeCopied(true); setTimeout(() => setCodeCopied(false), 2000);
  }

  async function refreshCode() {
    if (!confirm('This will invalidate the current koppelcode. Existing members keep their access. Continue?')) return;
    setRefreshing(true);
    try {
      const res = await api.post<{ koppelCode: string }>('/companies/my/refresh-code', {});
      setCompany(c => c ? { ...c, koppelCode: res.koppelCode } : c);
    } finally { setRefreshing(false); }
  }

  async function removeMember(userId: number) {
    if (!confirm('Remove this member from the company?')) return;
    setRemovingId(userId);
    try {
      await api.delete(`/companies/my/members/${userId}`);
      setCompany(c => c ? { ...c, companyUsers: c.companyUsers.filter(m => m.userId !== userId) } : c);
    } finally { setRemovingId(null); }
  }

  async function sendInvite(e: FormEvent) {
    e.preventDefault();
    setInviting(true); setInviteStatus(null);
    try {
      const res = await api.post<{ message: string }>('/companies/my/invite', { email: inviteEmail });
      setInviteStatus({ type: 'success', msg: res.message ?? `Invitation sent to ${inviteEmail}.` });
      // Refresh pending invites list
      const inv = await api.get<PendingInvite[]>('/companies/my/invites').catch(() => []);
      setPendingInvites(inv);
      setInviteEmail('');
    } catch (err: any) {
      setInviteStatus({ type: 'error', msg: err.message ?? 'Failed to send invite.' });
    } finally { setInviting(false); }
  }

  async function revokeInvite(id: number) {
    setRevokingId(id);
    try {
      await api.delete(`/companies/my/invites/${id}`);
      setPendingInvites(p => p.filter(i => i.id !== id));
    } finally { setRevokingId(null); }
  }

  async function handleRequest(id: number, action: 'approve' | 'reject') {
    setProcessingId(id);
    try {
      await api.post(`/companies/my/requests/${id}/${action}`, {});
      setRequests(r => r.filter(req => req.id !== id));
      if (action === 'approve') load();
    } finally { setProcessingId(null); }
  }

  async function handleCreate(e: FormEvent) {
    e.preventDefault();
    if (!createForm.confirmedRep) { setCreateError('You must confirm you are an authorized representative.'); return; }
    setCreateLoading(true); setCreateError('');
    try {
      await api.post('/companies', { name: createForm.name, vatNumber: createForm.vatNumber || undefined, confirmedRepresentative: true });
      await load();
    } catch (err: any) { setCreateError(err.message ?? 'Failed.'); }
    finally { setCreateLoading(false); }
  }

  async function handleJoin(e: FormEvent) {
    e.preventDefault();
    setJoinLoading(true); setJoinError(''); setJoinSuccess('');
    try {
      const res = await api.post<{ message: string }>('/companies/join', { koppelCode });
      setJoinSuccess(res.message);
    } catch (err: any) { setJoinError(err.message ?? 'Invalid koppelcode.'); }
    finally { setJoinLoading(false); }
  }

  if (company === undefined) return <p className="text-sm" style={{ color: '#8f9b9b' }}>Loading…</p>;

  // ── No company: setup wizard ──
  if (!company) return (
    <div>
      <p className="text-sm mb-6" style={{ color: '#8f9b9b' }}>Link your account to a company to share warranty registrations and claims with your team.</p>

      <div className="flex mb-5 rounded-lg overflow-hidden" style={{ border: '1px solid #e5e5e5', background: '#f4f4f4' }}>
        {([['create', 'Create company'], ['join', 'Join existing']] as ['create' | 'join', string][]).map(([t, label]) => (
          <button key={t} onClick={() => { setSetupTab(t); setCreateError(''); setJoinError(''); }} className="flex-1 py-2.5 text-sm font-medium"
            style={{ background: setupTab === t ? '#fff' : 'transparent', color: setupTab === t ? '#0b0d0e' : '#8f9b9b', border: 'none', borderRadius: 6, margin: 3, cursor: 'pointer' }}>
            {label}
          </button>
        ))}
      </div>

      {setupTab === 'create' && (
        <div className="space-y-4">
          <p className="text-sm" style={{ color: '#374151' }}>Register your company. You'll become the company admin and can add your team.</p>
          {createError && <div className="alert-error">{createError}</div>}
          <div><label className="label">Company name <span style={{ color: '#e00000' }}>*</span></label>
            <input className="input" required value={createForm.name} onChange={e => setCreateForm(f => ({ ...f, name: e.target.value }))} placeholder="Acme Corp" /></div>
          <div><label className="label">VAT number <span style={{ color: '#8f9b9b', fontWeight: 400 }}>(optional)</span></label>
            <input className="input" value={createForm.vatNumber} onChange={e => setCreateForm(f => ({ ...f, vatNumber: e.target.value }))} placeholder="NL123456789B01" /></div>
          <div className="flex gap-3 items-start p-4 rounded-lg" style={{ background: createForm.confirmedRep ? '#f0fdf4' : '#fafafa', border: `1px solid ${createForm.confirmedRep ? '#86efac' : '#e5e5e5'}`, cursor: 'pointer' }}
            onClick={() => setCreateForm(f => ({ ...f, confirmedRep: !f.confirmedRep }))}>
            <div className="flex-shrink-0 w-5 h-5 rounded flex items-center justify-center mt-0.5"
              style={{ background: createForm.confirmedRep ? '#16a34a' : '#fff', border: `2px solid ${createForm.confirmedRep ? '#16a34a' : '#d9d9d9'}` }}>
              {createForm.confirmedRep && <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>}
            </div>
            <p className="text-sm" style={{ color: '#374151', lineHeight: 1.6 }}>
              I confirm I am an authorized representative of <strong>{createForm.name || 'this company'}</strong> and am permitted to create this account on its behalf.
            </p>
          </div>
          <button className="btn-primary" onClick={handleCreate as any} disabled={createLoading || !createForm.confirmedRep}>
            {createLoading ? 'Creating…' : 'Create company account'}
          </button>
        </div>
      )}

      {setupTab === 'join' && (
        <div className="space-y-4">
          {joinSuccess ? (
            <div className="p-4 rounded-lg" style={{ background: '#f0fdf4', border: '1px solid #86efac' }}>
              <p className="text-sm font-semibold mb-1" style={{ color: '#15803d' }}>Request submitted</p>
              <p className="text-sm" style={{ color: '#15803d' }}>{joinSuccess}</p>
            </div>
          ) : (
            <>
              <p className="text-sm" style={{ color: '#374151' }}>Ask your company admin for the koppelcode and enter it below to request access.</p>
              {joinError && <div className="alert-error">{joinError}</div>}
              <div>
                <label className="label">Koppelcode</label>
                <input className="input" value={koppelCode} onChange={e => setKoppelCode(e.target.value.toUpperCase())}
                  placeholder="XXXX-XXXX" maxLength={9} style={{ fontFamily: 'monospace', letterSpacing: '0.1em', fontSize: 16 }} />
              </div>
              <button className="btn-primary" onClick={handleJoin as any} disabled={joinLoading}>{joinLoading ? 'Sending…' : 'Request access'}</button>
            </>
          )}
        </div>
      )}
    </div>
  );

  // ── Has company: management UI ──
  return (
    <div className="space-y-6">
      {/* Company header */}
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-lg font-bold" style={{ color: '#0b0d0e' }}>{company.name}</h2>
          <p className="text-sm" style={{ color: '#8f9b9b' }}>
            {company.companyUsers.length} member{company.companyUsers.length !== 1 ? 's' : ''}
            {company.vatNumber && ` · ${company.vatNumber}`}
          </p>
        </div>
        <span className="text-xs font-semibold uppercase tracking-wider px-2.5 py-1 rounded"
          style={{ background: isOwner ? '#fff0f0' : '#f4f4f4', color: isOwner ? '#e00000' : '#8f9b9b', border: `1px solid ${isOwner ? '#fca5a5' : '#e5e5e5'}` }}>
          {isOwner ? 'Owner' : 'Member'}
        </span>
      </div>

      {/* Koppelcode (owners only) */}
      {isOwner && company.koppelCode && (
        <div className="p-4 rounded-lg" style={{ background: '#fafafa', border: '1px solid #e5e5e5' }}>
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-sm font-semibold mb-1" style={{ color: '#0b0d0e' }}>Koppelcode</p>
              <p className="text-xs mb-3" style={{ color: '#8f9b9b' }}>Share with colleagues so they can request to join this company account.</p>
              <div className="flex items-center gap-2">
                <span className="font-mono font-bold text-lg tracking-widest px-3 py-1.5 rounded"
                  style={{ background: '#fff', border: '1px solid #e5e5e5', letterSpacing: '0.2em', color: '#0b0d0e' }}>
                  {company.koppelCode}
                </span>
                <button onClick={copyCode} className="btn-secondary text-xs">{codeCopied ? '✓ Copied' : 'Copy'}</button>
              </div>
            </div>
            <button onClick={refreshCode} disabled={refreshing} className="text-xs flex items-center gap-1.5 flex-shrink-0"
              style={{ color: '#8f9b9b', background: 'none', border: 'none', cursor: 'pointer' }}
              title="Invalidates the old code">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
              </svg>
              {refreshing ? 'Refreshing…' : 'Refresh'}
            </button>
          </div>
        </div>
      )}

      {/* Invite by email (owners only) */}
      {isOwner && (
        <div className="p-4 rounded-lg" style={{ background: '#fafafa', border: '1px solid #e5e5e5' }}>
          <p className="text-sm font-semibold mb-1" style={{ color: '#0b0d0e' }}>Invite by email</p>
          <p className="text-xs mb-3" style={{ color: '#8f9b9b' }}>Send a direct invitation link to a colleague. They'll be added to your company once they accept.</p>
          <form onSubmit={sendInvite} className="flex gap-2">
            <input className="input flex-1" type="email" required placeholder="colleague@company.com"
              value={inviteEmail} onChange={e => { setInviteEmail(e.target.value); setInviteStatus(null); }} />
            <button className="btn-primary whitespace-nowrap" type="submit" disabled={inviting}>
              {inviting ? 'Sending…' : 'Send invite'}
            </button>
          </form>
          {inviteStatus && (
            <div className={`mt-2 text-sm ${inviteStatus.type === 'success' ? 'alert-success' : 'alert-error'}`}>
              {inviteStatus.msg}
            </div>
          )}
          {pendingInvites.length > 0 && (
            <div className="mt-4">
              <p className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: '#8f9b9b' }}>Pending invites</p>
              <div className="space-y-1.5">
                {pendingInvites.map(inv => (
                  <div key={inv.id} className="flex items-center justify-between gap-3 px-3 py-2 rounded" style={{ background: '#fff', border: '1px solid #e5e5e5' }}>
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: '#f59e0b' }} />
                      <span className="text-sm" style={{ color: '#0b0d0e' }}>{inv.email}</span>
                      <span className="text-xs" style={{ color: '#8f9b9b' }}>· expires {new Date(inv.expiresAt).toLocaleDateString()}</span>
                    </div>
                    <button onClick={() => revokeInvite(inv.id)} disabled={revokingId === inv.id}
                      className="text-xs" style={{ color: '#d9d9d9', background: 'none', border: 'none', cursor: 'pointer' }}
                      onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = '#dc2626'; }}
                      onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = '#d9d9d9'; }}>
                      {revokingId === inv.id ? 'Revoking…' : 'Revoke'}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Pending join requests */}
      {isOwner && requests.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <p className="text-sm font-semibold" style={{ color: '#0b0d0e' }}>Join requests</p>
            <span className="text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center" style={{ background: '#e00000', color: '#fff' }}>{requests.length}</span>
          </div>
          <div className="space-y-2">
            {requests.map(req => (
              <div key={req.id} className="flex items-center justify-between gap-3 p-3 rounded-lg" style={{ border: '1px solid #e5e5e5', background: '#fafafa' }}>
                <div>
                  <p className="text-sm font-medium" style={{ color: '#0b0d0e' }}>{[req.user.firstName, req.user.lastName].filter(Boolean).join(' ') || req.user.email}</p>
                  <p className="text-xs" style={{ color: '#8f9b9b' }}>{req.user.email}{req.user.companyName ? ` · ${req.user.companyName}` : ''}</p>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <button onClick={() => handleRequest(req.id, 'approve')} disabled={processingId === req.id}
                    className="text-xs font-semibold px-3 py-1.5 rounded" style={{ background: '#0b0d0e', color: '#fff' }}>Approve</button>
                  <button onClick={() => handleRequest(req.id, 'reject')} disabled={processingId === req.id}
                    className="text-xs font-semibold px-3 py-1.5 rounded" style={{ background: '#f4f4f4', color: '#374151', border: '1px solid #e5e5e5' }}>Reject</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Members */}
      <div>
        <p className="text-sm font-semibold mb-3" style={{ color: '#0b0d0e' }}>Team members</p>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide border-b" style={{ borderColor: '#f0f0f0', color: '#8f9b9b' }}>
              <th className="pb-2">Name</th>
              <th className="pb-2">Email</th>
              <th className="pb-2">Role</th>
              {isOwner && <th />}
            </tr>
          </thead>
          <tbody>
            {company.companyUsers.map(m => {
              const isMe = m.userId === authUserId;
              return (
                <tr key={m.userId} className="border-b" style={{ borderColor: '#f9f9f9' }}>
                  <td className="py-2.5 font-medium" style={{ color: '#0b0d0e' }}>
                    {[m.user.firstName, m.user.lastName].filter(Boolean).join(' ') || '—'}
                    {isMe && <span className="ml-1 text-xs font-normal" style={{ color: '#8f9b9b' }}>(you)</span>}
                  </td>
                  <td className="py-2.5" style={{ color: '#8f9b9b' }}>{m.user.email}</td>
                  <td className="py-2.5">
                    <span className="text-xs font-semibold px-2 py-0.5 rounded capitalize"
                      style={{ background: m.role === 'owner' ? '#fff0f0' : '#f4f4f4', color: m.role === 'owner' ? '#e00000' : '#8f9b9b' }}>
                      {m.role}
                    </span>
                  </td>
                  {isOwner && (
                    <td className="py-2.5 text-right">
                      {!isMe && (
                        <button onClick={() => removeMember(m.userId)} disabled={removingId === m.userId}
                          className="text-xs" style={{ color: '#d9d9d9', background: 'none', border: 'none', cursor: 'pointer' }}
                          onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = '#dc2626'; }}
                          onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = '#d9d9d9'; }}>
                          {removingId === m.userId ? 'Removing…' : 'Remove'}
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Password tab ──────────────────────────────────────────────────────────────

function PasswordTab() {
  const [pw, setPw] = useState({ current: '', next: '', confirm: '' });
  const [status, setStatus] = useState<{ type: 'success' | 'error'; msg: string } | null>(null);
  const [loading, setLoading] = useState(false);

  async function save(e: FormEvent) {
    e.preventDefault();
    if (pw.next !== pw.confirm) { setStatus({ type: 'error', msg: 'New passwords do not match.' }); return; }
    setLoading(true); setStatus(null);
    try {
      const d = await api.post<{ message: string }>('/account/change-password', { currentPassword: pw.current, newPassword: pw.next });
      setStatus({ type: 'success', msg: d.message });
      setPw({ current: '', next: '', confirm: '' });
    } catch (e: any) {
      setStatus({ type: 'error', msg: e.message ?? 'Failed.' });
    } finally { setLoading(false); }
  }

  return (
    <form onSubmit={save} className="space-y-4">
      <div><label className="label">Current password</label><input className="input" type="password" value={pw.current} onChange={e => setPw(p => ({ ...p, current: e.target.value }))} required /></div>
      <div><label className="label">New password</label><input className="input" type="password" value={pw.next} onChange={e => setPw(p => ({ ...p, next: e.target.value }))} required minLength={8} /></div>
      <div><label className="label">Confirm new password</label><input className="input" type="password" value={pw.confirm} onChange={e => setPw(p => ({ ...p, confirm: e.target.value }))} required /></div>
      {status && <div className={status.type === 'success' ? 'alert-success' : 'alert-error'}>{status.msg}</div>}
      <button className="btn-primary" type="submit" disabled={loading}>{loading ? 'Updating…' : 'Update password'}</button>
    </form>
  );
}

// ── Privacy tab ───────────────────────────────────────────────────────────────

function PrivacyTab() {
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleteStatus, setDeleteStatus] = useState<string | null>(null);

  async function downloadData() {
    const token = localStorage.getItem('mw_token');
    const BASE = (import.meta.env.VITE_API_URL as string | undefined) ?? '/api';
    const r = await fetch(`${BASE}/account/export`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'montipower-data-export.json'; a.click();
    URL.revokeObjectURL(url);
  }

  async function requestDeletion() {
    try {
      const d = await api.post<{ message: string }>('/account/delete-request', {});
      setDeleteStatus(d.message ?? 'Request submitted.');
    } catch { setDeleteStatus('Request submitted. We will action it within 30 days.'); }
    setDeleteConfirm(false);
  }

  return (
    <div>
      <p className="text-sm mb-6" style={{ color: '#374151' }}>Under GDPR and CCPA you have the right to access, download, and request deletion of your personal data.</p>
      <div className="space-y-4">
        <div className="flex items-start gap-4 p-4 rounded-lg border" style={{ borderColor: '#d9d9d9', background: '#fbfbfb' }}>
          <div className="flex-1">
            <p className="font-semibold text-sm" style={{ color: '#0b0d0e' }}>Download your data</p>
            <p className="text-xs mt-0.5" style={{ color: '#8f9b9b' }}>JSON export of your profile, registered equipment, and warranty claims.</p>
          </div>
          <button onClick={downloadData} className="btn-secondary whitespace-nowrap">Download</button>
        </div>
        <div className="flex items-start gap-4 p-4 rounded-lg border" style={{ borderColor: '#fca5a5', background: '#fff5f5' }}>
          <div className="flex-1">
            <p className="font-semibold text-sm" style={{ color: '#991b1b' }}>Request account deletion</p>
            <p className="text-xs mt-0.5" style={{ color: '#b91c1c' }}>Permanently delete your account and all associated data within 30 days.</p>
          </div>
          {!deleteConfirm ? (
            <button onClick={() => setDeleteConfirm(true)} className="whitespace-nowrap text-sm font-semibold px-4 py-2 rounded border"
              style={{ color: '#dc2626', borderColor: '#fca5a5', background: 'white' }}>Request deletion</button>
          ) : (
            <div className="flex gap-2 flex-shrink-0">
              <button onClick={requestDeletion} className="text-sm font-semibold px-4 py-2 rounded" style={{ background: '#dc2626', color: 'white' }}>Confirm</button>
              <button onClick={() => setDeleteConfirm(false)} className="btn-secondary">Cancel</button>
            </div>
          )}
        </div>
        {deleteStatus && <div className="alert-success">{deleteStatus}</div>}
      </div>
    </div>
  );
}

// ── Language tab ──────────────────────────────────────────────────────────────

const LANGS = [
  { code: 'en', flag: '🇬🇧', label: 'English' },
  { code: 'es', flag: '🇪🇸', label: 'Español (LATAM)' },
  { code: 'de', flag: '🇩🇪', label: 'Deutsch' },
] as const;

function LanguageTab() {
  const { t, i18n } = useTranslation();
  const [saved, setSaved] = useState(false);

  function chooseLang(code: string) {
    i18n.changeLanguage(code);
    localStorage.setItem('mw_lang', code);
    // Persist to server (best-effort — no blocking)
    api.patch('/account/me', { language: code }).catch(() => {});
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  }

  return (
    <div>
      <p className="font-semibold text-sm mb-1" style={{ color: '#0b0d0e' }}>{t('settings.languageLabel')}</p>
      <p className="text-xs mb-5" style={{ color: '#8f9b9b' }}>{t('settings.languageDesc')}</p>
      <div className="space-y-2">
        {LANGS.map(l => {
          const active = i18n.language === l.code;
          return (
            <button key={l.code} onClick={() => chooseLang(l.code)}
              style={{
                display: 'flex', alignItems: 'center', gap: 12, width: '100%', padding: '12px 16px',
                borderRadius: 10, border: active ? '2px solid #e00000' : '1px solid #e5e5e5',
                background: active ? '#fff5f5' : '#fff', cursor: 'pointer', textAlign: 'left',
              }}>
              <span style={{ fontSize: 24 }}>{l.flag}</span>
              <div>
                <p style={{ fontWeight: 600, fontSize: 14, color: active ? '#e00000' : '#0b0d0e' }}>{l.label}</p>
              </div>
              {active && <span style={{ marginLeft: 'auto', fontSize: 12, color: '#e00000', fontWeight: 600 }}>✓ Active</span>}
            </button>
          );
        })}
      </div>
      {saved && <p className="text-sm mt-4" style={{ color: '#16a34a' }}>✓ {t('settings.saved')}</p>}
    </div>
  );
}
